import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, type Brand } from "../lib/api.ts";
import { useBrands } from "../lib/brand.tsx";
import { Icon } from "../lib/icons.tsx";

type Field = { key: string; label: string; area?: boolean; ph?: string };
const SECTIONS: { title: string; fields: Field[] }[] = [
  {
    title: "ธุรกิจ & ข้อเสนอ",
    fields: [
      { key: "businessType", label: "ประเภทธุรกิจ (Business Type)", ph: "เช่น รถมือสอง / คอร์สออนไลน์" },
      { key: "industry", label: "อุตสาหกรรม (Industry)" },
      { key: "product", label: "สินค้า (Product)" },
      { key: "service", label: "บริการ (Service)" },
      { key: "price", label: "ราคา (Price)" },
      { key: "offer", label: "ข้อเสนอ (Offer)", area: true },
      { key: "usp", label: "จุดขายที่แตกต่าง (USP)", area: true },
      { key: "positioning", label: "การวางตำแหน่ง (Positioning)", area: true },
    ],
  },
  {
    title: "ลูกค้า (Customer)",
    fields: [
      { key: "target", label: "กลุ่มเป้าหมาย (Target)", area: true },
      { key: "pain", label: "ความเจ็บปวด (Pain)", area: true },
      { key: "desire", label: "ความต้องการ (Desire)", area: true },
      { key: "objection", label: "ข้อโต้แย้ง (Objection)", area: true },
      { key: "buyingTrigger", label: "ตัวกระตุ้นการซื้อ (Buying Trigger)", area: true },
    ],
  },
  {
    title: "แบรนด์ & โทน (Brand & CI)",
    fields: [
      { key: "brandPersonality", label: "บุคลิกแบรนด์ (Personality)" },
      { key: "toneOfVoice", label: "โทนเสียง (Tone of Voice)" },
      { key: "colors", label: "สี (Colors)" },
      { key: "visualStyle", label: "สไตล์ภาพ (Visual Style)" },
    ],
  },
  {
    title: "ช่องทาง (Channels)",
    fields: [
      { key: "salesChannel", label: "ช่องทางขาย (Sales Channel)" },
      { key: "website", label: "เว็บไซต์ (Website)" },
      { key: "facebook", label: "Facebook" },
      { key: "tiktok", label: "TikTok" },
      { key: "youtube", label: "YouTube" },
      { key: "line", label: "LINE" },
      { key: "salesPage", label: "Sales Page" },
    ],
  },
  {
    title: "หลักฐาน & บันทึก (Proof & Notes)",
    fields: [
      { key: "competitor", label: "คู่แข่ง (Competitor)", area: true },
      { key: "customerReview", label: "รีวิวลูกค้า (Reviews)", area: true },
      { key: "caseStudy", label: "เคสตัวอย่าง (Case Study)", area: true },
      { key: "salesProcess", label: "กระบวนการขาย (Sales Process)", area: true },
      { key: "notes", label: "บันทึกเพิ่มเติม (Notes)", area: true },
    ],
  },
];

export default function BrandDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { reload } = useBrands();
  const [brand, setBrand] = useState<Brand | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [completeness, setCompleteness] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get<{ brand: Brand; profile: any }>(`/brands/${id}`).then((r) => {
      setBrand(r.brand);
      const p = r.profile || {};
      const f: Record<string, string> = {};
      SECTIONS.forEach((s) => s.fields.forEach((fl) => { f[fl.key] = p[fl.key] ?? ""; }));
      setForm(f);
      setCompleteness(p.completeness ?? 0);
    }).finally(() => setLoading(false));
  }, [id]);

  function set(k: string, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
    setSaved(false);
  }

  async function save() {
    if (!id) return;
    setSaving(true);
    try {
      const r = await api.put<{ profile: any }>(`/brands/${id}`, { profile: form });
      setCompleteness(r.profile?.completeness ?? 0);
      setSaved(true);
      reload();
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="page"><div className="spinner spinner--dark" /></div>;
  if (!brand) return <div className="page">ไม่พบแบรนด์</div>;

  return (
    <div className="page">
      <button className="mh-btn mh-btn--ghost mh-btn--sm" onClick={() => nav("/brands")} style={{ marginBottom: 14 }}>← กลับ</button>
      <div className="page__head mh-spread">
        <div>
          <div className="page__title">{brand.name}</div>
          <div className="page__subtitle">Brand Brain — ยิ่งกรอกครบ AI ยิ่งแม่นในทุกโมดูล</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="mh-sec" style={{ fontSize: 13 }}>ความสมบูรณ์</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: completeness >= 70 ? "var(--green-600)" : completeness >= 40 ? "var(--amber-600)" : "var(--gray-500)" }}>{completeness}%</div>
        </div>
      </div>

      <div style={{ height: 8, background: "var(--gray-100)", borderRadius: 999, overflow: "hidden", marginBottom: 24 }}>
        <div style={{ width: `${completeness}%`, height: "100%", background: "var(--blue-500)", transition: "width 0.3s" }} />
      </div>

      {SECTIONS.map((sec) => (
        <div className="mh-card card-pad" style={{ marginBottom: 18 }} key={sec.title}>
          <h3 style={{ fontSize: 15, marginBottom: 14 }}>{sec.title}</h3>
          <div className="form-grid">
            {sec.fields.map((fl) => (
              <div className="mh-field" style={{ margin: 0, gridColumn: fl.area ? "1 / -1" : undefined }} key={fl.key}>
                <label className="mh-label">{fl.label}</label>
                {fl.area
                  ? <textarea className="mh-textarea" value={form[fl.key] ?? ""} onChange={(e) => set(fl.key, e.target.value)} placeholder={fl.ph} />
                  : <input className="mh-input" value={form[fl.key] ?? ""} onChange={(e) => set(fl.key, e.target.value)} placeholder={fl.ph} />}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ position: "sticky", bottom: 0, padding: "14px 0", display: "flex", gap: 12, alignItems: "center", background: "linear-gradient(transparent, var(--bg-app) 40%)" }}>
        <button className="mh-btn mh-btn--primary mh-btn--lg" onClick={save} disabled={saving}>
          {saving ? <span className="spinner" /> : <><Icon name="Save" size={18} /> บันทึก Brand Brain</>}
        </button>
        {saved && <span className="mh-pill mh-pill--winner">บันทึกแล้ว ✓</span>}
      </div>
    </div>
  );
}
